/**
 * Authentication Service
 * Handles OAuth 2.0, API keys, and JWT token authentication for integrations
 */

import { APIRequest } from './APIGateway';

export interface OAuthTokens {
  accessToken: string;
  refreshToken?: string;
  tokenType: string;
  expiresIn?: number;
  expiresAt?: Date;
  scope?: string;
}

export interface APIKeyCredentials {
  apiKey: string;
  apiSecret?: string;
  keyLocation: 'header' | 'query' | 'body';
  keyName: string;
  secretName?: string;
}

export interface JWTCredentials {
  token: string;
  algorithm: string;
  expiresAt?: Date;
  issuer?: string;
  audience?: string;
}

export interface AuthCredentials {
  type: 'oauth' | 'api_key' | 'jwt' | 'basic' | 'bearer';
  oauth?: OAuthTokens;
  apiKey?: APIKeyCredentials;
  jwt?: JWTCredentials;
  basic?: { username: string; password: string };
  bearer?: { token: string };
  customHeaders?: Record<string, string>;
}

export interface AuthResult {
  success: boolean;
  credentials?: AuthCredentials;
  error?: string;
  expiresAt?: Date;
}

export class AuthenticationService {
  private tokenCache: Map<string, CachedToken> = new Map();
  private refreshPromises: Map<string, Promise<OAuthTokens>> = new Map();

  /**
   * Authenticate an API request by adding appropriate headers
   */
  async authenticateRequest(request: APIRequest, credentials?: AuthCredentials): Promise<APIRequest> {
    if (!credentials) {
      return request; // No authentication required
    }

    const authenticatedRequest = { ...request };

    switch (credentials.type) {
      case 'oauth':
        return await this.addOAuthHeaders(authenticatedRequest, credentials.oauth!);
      
      case 'api_key':
        return this.addAPIKeyAuth(authenticatedRequest, credentials.apiKey!);
      
      case 'jwt':
        return this.addJWTAuth(authenticatedRequest, credentials.jwt!);
      
      case 'basic':
        return this.addBasicAuth(authenticatedRequest, credentials.basic!);
      
      case 'bearer':
        return this.addBearerAuth(authenticatedRequest, credentials.bearer!);
      
      default:
        throw new Error(`Unsupported authentication type: ${credentials.type}`);
    }
  }

  /**
   * Add OAuth 2.0 authentication headers
   */
  private async addOAuthHeaders(request: APIRequest, oauth: OAuthTokens): Promise<APIRequest> {
    // Check if token needs refresh
    if (this.isTokenExpired(oauth)) {
      if (!oauth.refreshToken) {
        throw new Error('OAuth token expired and no refresh token available');
      }
      
      const refreshedTokens = await this.refreshOAuthToken(oauth);
      oauth = refreshedTokens;
    }

    return {
      ...request,
      headers: {
        ...request.headers,
        'Authorization': `${oauth.tokenType} ${oauth.accessToken}`
      }
    };
  }

  /**
   * Add API key authentication
   */
  private addAPIKeyAuth(request: APIRequest, apiKey: APIKeyCredentials): APIRequest {
    const authenticatedRequest = { ...request };

    switch (apiKey.keyLocation) {
      case 'header':
        authenticatedRequest.headers = {
          ...authenticatedRequest.headers,
          [apiKey.keyName]: apiKey.apiKey
        };
        
        if (apiKey.apiSecret && apiKey.secretName) {
          authenticatedRequest.headers[apiKey.secretName] = apiKey.apiSecret;
        }
        break;

      case 'query':
        const url = new URL(authenticatedRequest.url);
        url.searchParams.set(apiKey.keyName, apiKey.apiKey);
        
        if (apiKey.apiSecret && apiKey.secretName) {
          url.searchParams.set(apiKey.secretName, apiKey.apiSecret);
        }
        
        authenticatedRequest.url = url.toString();
        break;

      case 'body':
        if (!authenticatedRequest.body) {
          authenticatedRequest.body = {};
        }
        
        authenticatedRequest.body[apiKey.keyName] = apiKey.apiKey;
        
        if (apiKey.apiSecret && apiKey.secretName) {
          authenticatedRequest.body[apiKey.secretName] = apiKey.apiSecret;
        }
        break;
    }

    return authenticatedRequest;
  }

  /**
   * Add JWT authentication
   */
  private addJWTAuth(request: APIRequest, jwt: JWTCredentials): APIRequest {
    if (jwt.expiresAt && jwt.expiresAt < new Date()) {
      throw new Error('JWT token has expired');
    }

    return {
      ...request,
      headers: {
        ...request.headers,
        'Authorization': `Bearer ${jwt.token}`
      }
    };
  }

  /**
   * Add Basic authentication
   */
  private addBasicAuth(request: APIRequest, basic: { username: string; password: string }): APIRequest {
    const credentials = btoa(`${basic.username}:${basic.password}`);
    
    return {
      ...request,
      headers: {
        ...request.headers,
        'Authorization': `Basic ${credentials}`
      }
    };
  }

  /**
   * Add Bearer token authentication
   */
  private addBearerAuth(request: APIRequest, bearer: { token: string }): APIRequest {
    return {
      ...request,
      headers: {
        ...request.headers,
        'Authorization': `Bearer ${bearer.token}`
      }
    };
  }

  /**
   * Check if OAuth token is expired
   */
  private isTokenExpired(oauth: OAuthTokens): boolean {
    if (!oauth.expiresAt) {
      return false; // No expiration info, assume valid
    }
    
    // Add 5-minute buffer before actual expiration
    const bufferTime = 5 * 60 * 1000; // 5 minutes in milliseconds
    return oauth.expiresAt.getTime() - bufferTime < Date.now();
  }

  /**
   * Refresh OAuth token
   */
  private async refreshOAuthToken(oauth: OAuthTokens): Promise<OAuthTokens> {
    const cacheKey = oauth.refreshToken!;
    
    // Check if refresh is already in progress
    if (this.refreshPromises.has(cacheKey)) {
      return await this.refreshPromises.get(cacheKey)!;
    }

    // Start refresh process
    const refreshPromise = this.performTokenRefresh(oauth);
    this.refreshPromises.set(cacheKey, refreshPromise);

    try {
      const refreshedTokens = await refreshPromise;
      
      // Cache the new tokens
      this.cacheToken(cacheKey, refreshedTokens);
      
      return refreshedTokens;
    } finally {
      // Clean up refresh promise
      this.refreshPromises.delete(cacheKey);
    }
  }

  /**
   * Perform the actual token refresh
   */
  private async performTokenRefresh(oauth: OAuthTokens): Promise<OAuthTokens> {
    // This is a generic implementation - specific platforms would override this
    const refreshRequest: APIRequest = {
      method: 'POST',
      url: '/oauth/token', // Platform-specific endpoint
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: new URLSearchParams({
        grant_type: 'refresh_token',
        refresh_token: oauth.refreshToken!
      }).toString()
    };

    try {
      const response = await fetch(refreshRequest.url, {
        method: refreshRequest.method,
        headers: refreshRequest.headers,
        body: refreshRequest.body
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.status} ${response.statusText}`);
      }

      const tokenData = await response.json();
      
      return {
        accessToken: tokenData.access_token,
        refreshToken: tokenData.refresh_token || oauth.refreshToken,
        tokenType: tokenData.token_type || 'Bearer',
        expiresIn: tokenData.expires_in,
        expiresAt: tokenData.expires_in ? 
          new Date(Date.now() + tokenData.expires_in * 1000) : 
          undefined,
        scope: tokenData.scope
      };

    } catch (error) {
      throw new Error(`Failed to refresh OAuth token: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  }

  /**
   * Cache token for reuse
   */
  private cacheToken(key: string, tokens: OAuthTokens): void {
    this.tokenCache.set(key, {
      tokens,
      cachedAt: new Date()
    });
  }

  /**
   * Get cached token
   */
  private getCachedToken(key: string): OAuthTokens | null {
    const cached = this.tokenCache.get(key);
    if (!cached) {
      return null;
    }

    // Check if cached token is still valid
    if (this.isTokenExpired(cached.tokens)) {
      this.tokenCache.delete(key);
      return null;
    }

    return cached.tokens;
  }

  /**
   * Validate credentials
   */
  async validateCredentials(credentials: AuthCredentials): Promise<AuthResult> {
    try {
      switch (credentials.type) {
        case 'oauth':
          return await this.validateOAuthCredentials(credentials.oauth!);
        
        case 'api_key':
          return await this.validateAPIKeyCredentials(credentials.apiKey!);
        
        case 'jwt':
          return await this.validateJWTCredentials(credentials.jwt!);
        
        case 'basic':
          return await this.validateBasicCredentials(credentials.basic!);
        
        case 'bearer':
          return await this.validateBearerCredentials(credentials.bearer!);
        
        default:
          return {
            success: false,
            error: `Unsupported authentication type: ${credentials.type}`
          };
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Unknown validation error'
      };
    }
  }

  private async validateOAuthCredentials(oauth: OAuthTokens): Promise<AuthResult> {
    if (!oauth.accessToken) {
      return { success: false, error: 'Missing access token' };
    }

    if (this.isTokenExpired(oauth)) {
      if (!oauth.refreshToken) {
        return { success: false, error: 'Token expired and no refresh token available' };
      }
      
      try {
        const refreshedTokens = await this.refreshOAuthToken(oauth);
        return {
          success: true,
          credentials: { type: 'oauth', oauth: refreshedTokens },
          expiresAt: refreshedTokens.expiresAt
        };
      } catch (error) {
        return { success: false, error: `Token refresh failed: ${error}` };
      }
    }

    return {
      success: true,
      credentials: { type: 'oauth', oauth },
      expiresAt: oauth.expiresAt
    };
  }

  private async validateAPIKeyCredentials(apiKey: APIKeyCredentials): Promise<AuthResult> {
    if (!apiKey.apiKey) {
      return { success: false, error: 'Missing API key' };
    }

    if (!apiKey.keyName) {
      return { success: false, error: 'Missing API key name' };
    }

    return {
      success: true,
      credentials: { type: 'api_key', apiKey }
    };
  }

  private async validateJWTCredentials(jwt: JWTCredentials): Promise<AuthResult> {
    if (!jwt.token) {
      return { success: false, error: 'Missing JWT token' };
    }

    if (jwt.expiresAt && jwt.expiresAt < new Date()) {
      return { success: false, error: 'JWT token has expired' };
    }

    return {
      success: true,
      credentials: { type: 'jwt', jwt },
      expiresAt: jwt.expiresAt
    };
  }

  private async validateBasicCredentials(basic: { username: string; password: string }): Promise<AuthResult> {
    if (!basic.username || !basic.password) {
      return { success: false, error: 'Missing username or password' };
    }

    return {
      success: true,
      credentials: { type: 'basic', basic }
    };
  }

  private async validateBearerCredentials(bearer: { token: string }): Promise<AuthResult> {
    if (!bearer.token) {
      return { success: false, error: 'Missing bearer token' };
    }

    return {
      success: true,
      credentials: { type: 'bearer', bearer }
    };
  }

  /**
   * Clear token cache
   */
  clearTokenCache(): void {
    this.tokenCache.clear();
    this.refreshPromises.clear();
  }

  /**
   * Get authentication statistics
   */
  getAuthStats(): AuthStats {
    return {
      cachedTokens: this.tokenCache.size,
      activeRefreshes: this.refreshPromises.size,
      cacheHitRate: this.calculateCacheHitRate()
    };
  }

  private calculateCacheHitRate(): number {
    // Implementation would track cache hits/misses
    return 0.85; // Placeholder
  }
}

interface CachedToken {
  tokens: OAuthTokens;
  cachedAt: Date;
}

export interface AuthStats {
  cachedTokens: number;
  activeRefreshes: number;
  cacheHitRate: number;
}